// ===========================================
// PREMIUM CONTACTS PAGE - VP-Level Design
// Matching Tripnovac Platform Hub Quality
// Modern CRM with Advanced Features
// ===========================================

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { contactsService, Contact as ApiContact } from '../../services/contacts.service';
import {
  Users,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  MoreHorizontal,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Tag,
  Star,
  StarOff,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  User,
  Building2,
  Globe,
  Clock,
  Send,
  Eye,
  MousePointerClick,
  UserPlus,
  SlidersHorizontal,
  Grid3X3,
  List,
  ArrowUpDown,
  ExternalLink,
  MessageSquare,
  Zap,
  Target,
  TrendingUp,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Copy,
  MoreVertical,
  Sparkles,
} from 'lucide-react';

// Helper to transform API contact to display format
const transformContact = (c: ApiContact): Contact => ({
  id: c.id,
  firstName: c.firstName || '',
  lastName: c.lastName || '',
  email: c.email,
  phone: c.phone || '',
  company: c.company || '',
  position: c.jobTitle || '',
  location: '',
  tags: c.tags || [],
  score: c.leadScore || 0,
  status: c.leadStatus || 'NEW',
  lastActivity: c.updatedAt ? new Date(c.updatedAt).toLocaleDateString() : 'Never',
  emailsSent: 0,
  emailsOpened: 0,
  avatar: null,
  starred: false,
  createdAt: c.createdAt,
});

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  position: string;
  location: string;
  tags: string[];
  score: number;
  status: string;
  lastActivity: string;
  emailsSent: number;
  emailsOpened: number;
  avatar: string | null;
  starred: boolean;
  createdAt: string;
}

// Stat Card Component
const StatCard: React.FC<{
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  gradient: string;
}> = ({ title, value, change, icon, gradient }) => (
  <motion.div
    whileHover={{ y: -2 }}
    className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm"
  >
    <div className="flex items-start justify-between">
      <div className={`w-11 h-11 rounded-xl ${gradient} flex items-center justify-center text-white shadow-lg`}>
        {icon}
      </div>
      {change !== undefined && (
        <span className={`flex items-center gap-1 text-xs font-bold ${change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          {change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingUp className="w-3 h-3 rotate-180" />}
          {change >= 0 ? '+' : ''}{change}%
        </span>
      )}
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mt-3">{value}</h3>
    <p className="text-sm text-gray-500">{title}</p>
  </motion.div>
);

// Contact Row Component
const ContactRow: React.FC<{
  contact: Contact;
  selected: boolean;
  onSelect: () => void;
  onStar: () => void;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onEmail?: () => void;
  viewMode: 'grid' | 'list';
}> = ({ contact, selected, onSelect, onStar, onView, onEdit, onDelete, onEmail, viewMode }) => {
  const [showMenu, setShowMenu] = useState(false);

  const scoreColor = contact.score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                     contact.score >= 60 ? 'bg-amber-100 text-amber-700' :
                     'bg-red-100 text-red-700';

  const tagColors: Record<string, string> = {
    VIP: 'bg-violet-100 text-violet-700',
    Enterprise: 'bg-blue-100 text-blue-700',
    Lead: 'bg-emerald-100 text-emerald-700',
    Startup: 'bg-amber-100 text-amber-700',
    Travel: 'bg-cyan-100 text-cyan-700',
    B2B: 'bg-purple-100 text-purple-700',
    Agency: 'bg-pink-100 text-pink-700',
    Partner: 'bg-indigo-100 text-indigo-700',
    Hospitality: 'bg-rose-100 text-rose-700',
  };

  if (viewMode === 'grid') {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ y: -4 }}
        className={`relative bg-white rounded-2xl border ${selected ? 'border-violet-400 ring-2 ring-violet-100' : 'border-gray-200'} p-5 shadow-sm hover:shadow-lg transition-all group`}
      >
        {/* Selection & Star */}
        <div className="absolute top-4 right-4 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={onStar}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
          >
            {contact.starred ? (
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
            ) : (
              <StarOff className="w-4 h-4 text-gray-400" />
            )}
          </motion.button>
          <div className="relative">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setShowMenu(!showMenu)}
              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
            >
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </motion.button>
            <AnimatePresence>
              {showMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-10"
                >
                  <button
                    onClick={() => { onView?.(); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" /> View
                  </button>
                  <button
                    onClick={() => { onEdit?.(); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Edit2 className="w-4 h-4" /> Edit
                  </button>
                  <button
                    onClick={() => { onEmail?.(); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" /> Email
                  </button>
                  <div className="my-2 border-t border-gray-100" />
                  <button
                    onClick={() => { onDelete?.(); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Avatar & Basic Info */}
        <div className="flex items-center gap-4 mb-4">
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
              {contact.firstName[0]}{contact.lastName[0]}
            </div>
            {contact.starred && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center shadow">
                <Star className="w-3 h-3 text-white fill-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">
              {contact.firstName} {contact.lastName}
            </h3>
            <p className="text-sm text-gray-500 truncate">{contact.position}</p>
          </div>
        </div>

        {/* Company */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
          <Building2 className="w-4 h-4 text-gray-400" />
          <span className="truncate">{contact.company}</span>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {contact.tags.map(tag => (
            <span key={tag} className={`px-2 py-0.5 rounded-full text-xs font-medium ${tagColors[tag] || 'bg-gray-100 text-gray-600'}`}>
              {tag}
            </span>
          ))}
        </div>

        {/* Score & Activity */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-lg text-xs font-bold ${scoreColor}`}>
              {contact.score} score
            </span>
          </div>
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {contact.lastActivity}
          </span>
        </div>

        {/* Quick Actions on Hover */}
        <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-white via-white to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-b-2xl">
          <div className="flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => { e.stopPropagation(); onEmail?.(); }}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-violet-100 text-violet-700 text-xs font-medium hover:bg-violet-200 transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              Email
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => { e.stopPropagation(); onView?.(); }}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200 transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
              View
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  // List View
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex items-center gap-4 p-4 rounded-xl ${selected ? 'bg-violet-50 border border-violet-200' : 'bg-white hover:bg-gray-50'} transition-colors group`}
    >
      {/* Checkbox */}
      <label className="relative cursor-pointer">
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          className="sr-only"
        />
        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
          selected ? 'bg-violet-600 border-violet-600' : 'border-gray-300 group-hover:border-violet-400'
        }`}>
          {selected && <Check className="w-3 h-3 text-white" />}
        </div>
      </label>

      {/* Star */}
      <button onClick={onStar} className="flex-shrink-0">
        {contact.starred ? (
          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
        ) : (
          <StarOff className="w-4 h-4 text-gray-300 hover:text-amber-400" />
        )}
      </button>

      {/* Avatar */}
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
        {contact.firstName[0]}{contact.lastName[0]}
      </div>

      {/* Name & Email */}
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-gray-900">{contact.firstName} {contact.lastName}</h4>
        <p className="text-sm text-gray-500 truncate">{contact.email}</p>
      </div>

      {/* Company */}
      <div className="hidden md:block w-40 truncate">
        <p className="text-sm text-gray-900">{contact.company}</p>
        <p className="text-xs text-gray-500">{contact.position}</p>
      </div>

      {/* Tags */}
      <div className="hidden lg:flex items-center gap-1 w-32">
        {contact.tags.slice(0, 2).map(tag => (
          <span key={tag} className={`px-2 py-0.5 rounded-full text-xs font-medium ${tagColors[tag] || 'bg-gray-100 text-gray-600'}`}>
            {tag}
          </span>
        ))}
        {contact.tags.length > 2 && (
          <span className="text-xs text-gray-400">+{contact.tags.length - 2}</span>
        )}
      </div>

      {/* Score */}
      <div className="hidden md:block w-20">
        <span className={`px-2 py-1 rounded-lg text-xs font-bold ${scoreColor}`}>
          {contact.score}
        </span>
      </div>

      {/* Last Activity */}
      <div className="hidden lg:block w-24 text-sm text-gray-500">
        {contact.lastActivity}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={(e) => { e.stopPropagation(); onEmail?.(); }}
          className="w-8 h-8 rounded-lg hover:bg-violet-100 flex items-center justify-center text-gray-400 hover:text-violet-600"
        >
          <Mail className="w-4 h-4" />
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
          className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600"
        >
          <Edit2 className="w-4 h-4" />
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
          className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-gray-400 hover:text-red-600"
        >
          <Trash2 className="w-4 h-4" />
        </motion.button>
      </div>
    </motion.div>
  );
};

const PremiumContactsPage: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState<'name' | 'score' | 'activity'>('name');
  const [stats, setStats] = useState([
    { title: 'Total Contacts', value: '0', change: 0, icon: <Users className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-violet-500 to-purple-600' },
    { title: 'Active Today', value: '0', change: 0, icon: <Zap className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-emerald-500 to-teal-600' },
    { title: 'Avg. Score', value: '0', change: 0, icon: <Target className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-amber-500 to-orange-600' },
    { title: 'Emails Sent', value: '0', change: 0, icon: <Mail className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-blue-500 to-cyan-600' },
  ]);

  // New contact form state
  const [newContact, setNewContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    tags: [] as string[],
  });

  // Fetch contacts from API
  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await contactsService.list({
        page: currentPage,
        limit: 12,
        search: searchQuery || undefined,
        sortBy: sortBy === 'name' ? 'firstName' : sortBy === 'score' ? 'leadScore' : 'updatedAt',
        sortOrder: 'desc',
      });
      
      if (response && response.contacts) {
        setContacts(response.contacts.map(transformContact));
        setTotalContacts(response.total || 0);
        setTotalPages(response.totalPages || 1);
        
        // Update stats with real data
        setStats([
          { title: 'Total Contacts', value: (response.total || 0).toLocaleString(), change: 12, icon: <Users className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-violet-500 to-purple-600' },
          { title: 'Active Today', value: '0', change: 0, icon: <Zap className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-emerald-500 to-teal-600' },
          { title: 'Avg. Score', value: '0', change: 0, icon: <Target className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-amber-500 to-orange-600' },
          { title: 'Emails Sent', value: '0', change: 0, icon: <Mail className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-blue-500 to-cyan-600' },
        ]);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to load contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, sortBy]);

  // Fetch stats from API
  const fetchStats = useCallback(async () => {
    try {
      const statsData = await contactsService.getStats();
      if (statsData) {
        setStats([
          { title: 'Total Contacts', value: (statsData.total || 0).toLocaleString(), change: 12, icon: <Users className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-violet-500 to-purple-600' },
          { title: 'Subscribed', value: (statsData.subscribed || 0).toLocaleString(), change: 8, icon: <Zap className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-emerald-500 to-teal-600' },
          { title: 'Recently Added', value: (statsData.recentlyAdded || 0).toLocaleString(), change: 5, icon: <Target className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-amber-500 to-orange-600' },
          { title: 'By Status', value: Object.keys(statsData.byStatus || {}).length.toString(), change: 0, icon: <Mail className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-blue-500 to-cyan-600' },
        ]);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  // Load data on mount and when filters change
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Handle creating a new contact
  const handleCreateContact = async () => {
    try {
      if (!newContact.email) {
        alert('Email is required');
        return;
      }
      await contactsService.create({
        email: newContact.email,
        firstName: newContact.firstName,
        lastName: newContact.lastName,
        company: newContact.company,
        phone: newContact.phone,
        tags: newContact.tags,
      });
      setShowAddModal(false);
      setNewContact({ firstName: '', lastName: '', email: '', company: '', phone: '', tags: [] });
      fetchContacts();
      fetchStats();
    } catch (err) {
      console.error('Error creating contact:', err);
      alert('Failed to create contact');
    }
  };

  // Handle deleting contacts
  const handleDeleteSelected = async () => {
    if (selectedContacts.length === 0) return;
    if (!confirm(`Delete ${selectedContacts.length} contact(s)?`)) return;
    
    try {
      await contactsService.bulkDelete(selectedContacts);
      setSelectedContacts([]);
      fetchContacts();
      fetchStats();
    } catch (err) {
      console.error('Error deleting contacts:', err);
      alert('Failed to delete contacts');
    }
  };

  // Handle single delete
  const handleDeleteContact = async (id: string) => {
    if (!confirm('Delete this contact?')) return;
    try {
      await contactsService.delete(id);
      fetchContacts();
      fetchStats();
    } catch (err) {
      console.error('Error deleting contact:', err);
      alert('Failed to delete contact');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedContacts(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleStar = (id: string) => {
    setContacts(prev =>
      prev.map(c => c.id === id ? { ...c, starred: !c.starred } : c)
    );
  };

  const filteredContacts = contacts.filter(c =>
    !searchQuery ||
    `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.company.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white shadow-lg shadow-violet-500/25">
              <Users className="w-6 h-6" />
            </div>
            Contacts
          </h1>
          <p className="text-gray-600 mt-2">
            Manage your contacts and customer relationships
          </p>
        </div>

        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = '.csv,.xlsx';
              input.onchange = () => alert('Import feature coming soon. File selected: ' + input.files?.[0]?.name);
              input.click();
            }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors"
          >
            <Upload className="w-4 h-4" />
            Import
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              const csv = ['Name,Email,Phone,Company,Status'];
              contacts.forEach(c => csv.push([c.name, c.email, c.phone || '', c.company || '', c.status].join(',')));
              const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'contacts-export.csv';
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors"
          >
            <Download className="w-4 h-4" />
            Export
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium shadow-lg shadow-violet-500/25"
          >
            <Plus className="w-4 h-4" />
            Add Contact
          </motion.button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <StatCard {...stat} />
          </motion.div>
        ))}
      </div>

      {/* Search & Filters Bar */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search contacts by name, email, or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none text-gray-900 placeholder:text-gray-400"
            />
          </div>

          {/* Filter & View Options */}
          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-colors ${
                showFilters ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </motion.button>

            <div className="flex items-center bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white shadow text-violet-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white shadow text-violet-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            <button className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900">
              <ArrowUpDown className="w-4 h-4" />
              Sort
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="pt-4 mt-4 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</label>
                  <select className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-violet-500 outline-none">
                    <option>All Statuses</option>
                    <option>Active</option>
                    <option>Inactive</option>
                    <option>Bounced</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Tags</label>
                  <select className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-violet-500 outline-none">
                    <option>All Tags</option>
                    <option>VIP</option>
                    <option>Enterprise</option>
                    <option>Lead</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Score Range</label>
                  <select className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-violet-500 outline-none">
                    <option>All Scores</option>
                    <option>80-100 (Hot)</option>
                    <option>60-79 (Warm)</option>
                    <option>0-59 (Cold)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Date Added</label>
                  <select className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-violet-500 outline-none">
                    <option>Any Time</option>
                    <option>Last 7 Days</option>
                    <option>Last 30 Days</option>
                    <option>Last 90 Days</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Selected Actions */}
        {selectedContacts.length > 0 && (
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-4"
          >
            <span className="text-sm font-medium text-gray-700">
              {selectedContacts.length} selected
            </span>
            <div className="flex items-center gap-2">
              <button className="px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 text-sm font-medium hover:bg-violet-200 transition-colors flex items-center gap-1.5">
                <Mail className="w-4 h-4" />
                Send Email
              </button>
              <button className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors flex items-center gap-1.5">
                <Tag className="w-4 h-4" />
                Add Tags
              </button>
              <button 
                onClick={handleDeleteSelected}
                className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 transition-colors flex items-center gap-1.5"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
            <button
              onClick={() => setSelectedContacts([])}
              className="ml-auto text-sm text-gray-500 hover:text-gray-700"
            >
              Clear selection
            </button>
          </motion.div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-16">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading contacts...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="text-center py-16 bg-red-50 rounded-2xl border border-red-200">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchContacts}
            className="px-4 py-2 rounded-xl bg-red-100 text-red-700 font-medium hover:bg-red-200 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Contacts List/Grid */}
      {!loading && !error && (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' 
          : 'bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden divide-y divide-gray-100'
        }>
          {filteredContacts.map((contact, index) => (
            <ContactRow
              key={contact.id}
              contact={contact}
              selected={selectedContacts.includes(contact.id)}
              onSelect={() => toggleSelect(contact.id)}
              onStar={() => toggleStar(contact.id)}
              onView={() => alert(`View contact: ${contact.firstName} ${contact.lastName}`)}
              onEdit={() => alert(`Edit contact: ${contact.firstName} ${contact.lastName}`)}
              onDelete={() => handleDeleteContact(contact.id)}
              onEmail={() => alert(`Email to: ${contact.email}`)}
              viewMode={viewMode}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredContacts.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="min-h-[50vh] flex items-center justify-center"
        >
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Users className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No contacts found</h3>
            <p className="text-gray-500 mb-6">
              {totalContacts === 0 ? 'Get started by adding your first contact' : 'Try adjusting your search or filters'}
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setShowFilters(false);
              }}
              className="px-4 py-2 rounded-xl bg-violet-100 text-violet-700 font-medium hover:bg-violet-200 transition-colors"
            >
              Clear filters
            </button>
          </div>
        </motion.div>
      )}

      {/* Pagination */}
      {!loading && !error && filteredContacts.length > 0 && (
        <div className="flex items-center justify-between pt-4">
          <p className="text-sm text-gray-500">
            Showing <span className="font-medium">{filteredContacts.length}</span> of{' '}
            <span className="font-medium">{totalContacts.toLocaleString()}</span> contacts
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 rounded-xl border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-medium ${
                  page === currentPage
                    ? 'bg-violet-600 text-white'
                    : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            {totalPages > 5 && (
              <>
                <span className="text-gray-400">...</span>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-medium ${
                    totalPages === currentPage
                      ? 'bg-violet-600 text-white'
                      : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {totalPages}
                </button>
              </>
            )}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-10 h-10 rounded-xl border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      <AnimatePresence>
        {showAddModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAddModal(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white rounded-2xl shadow-2xl z-50 flex flex-col max-h-[90vh] overflow-hidden"
            >
              <div className="p-6 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">Add New Contact</h2>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">First Name</label>
                    <input
                      type="text"
                      value={newContact.firstName}
                      onChange={(e) => setNewContact(prev => ({ ...prev, firstName: e.target.value }))}
                      className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Last Name</label>
                    <input
                      type="text"
                      value={newContact.lastName}
                      onChange={(e) => setNewContact(prev => ({ ...prev, lastName: e.target.value }))}
                      className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Email *</label>
                  <input
                    type="email"
                    value={newContact.email}
                    onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                    placeholder="john@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    value={newContact.phone}
                    onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                    className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Company</label>
                  <input
                    type="text"
                    value={newContact.company}
                    onChange={(e) => setNewContact(prev => ({ ...prev, company: e.target.value }))}
                    className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                    placeholder="Company Name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Tags</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {['VIP', 'Enterprise', 'Lead', 'Startup'].map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => setNewContact(prev => ({
                          ...prev,
                          tags: prev.tags.includes(tag) 
                            ? prev.tags.filter(t => t !== tag)
                            : [...prev.tags, tag]
                        }))}
                        className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                          newContact.tags.includes(tag)
                            ? 'border-violet-500 bg-violet-100 text-violet-700'
                            : 'border-gray-200 text-gray-600 hover:border-violet-500 hover:text-violet-600'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="p-6 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3 flex-shrink-0">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2.5 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleCreateContact}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium shadow-lg shadow-violet-500/25 hover:opacity-90 transition-opacity"
                >
                  Add Contact
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PremiumContactsPage;
